import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AlertTriangle, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { useDolfinClient } from "@/components/DolfinProvider";
import { toastApiError } from "@/lib/api-error";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { formatCurrency, formatDateTime } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import { DeliveryStateBadge, type DeliveryState } from "@/components/EInvoiceDeliveryState";
import { EInvoiceReferenceSearch } from "@/components/EInvoiceReferenceSearch";

type SentEInvoice = components["schemas"]["OutboundEInvoiceView"];

const PAGE_SIZE = 25;

interface SentEInvoicesProps {
  /** Host-supplied link component, so the component can navigate without importing a router. */
  LinkComponent?: React.ComponentType<{ href: string; className?: string; children: ReactNode }>;
  onInvoiceClick?: (invoiceId: string) => void;
  /** Opens a credit note; credit-note rows link to their invoice when not supplied. */
  onCreditNoteClick?: (creditNoteId: string) => void;
  /** Whether the organisation is registered for e-invoicing, so an empty list can say why it is empty. */
  registered?: boolean | null;
}

const ALL = "all";

const FILTERS: { label: string; state?: DeliveryState }[] = [
  { label: "All" },
  { label: "Failed", state: "DeliveryFailed" },
  { label: "Sending", state: "Submitted" },
  // The network has it and has not ruled yet (SDI).
  { label: "Awaiting network", state: "Accepted" },
  { label: "Delivered", state: "Delivered" },
  // Issued but not delivered: the customer has to be told where to collect it.
  { label: "Not delivered", state: "MadeAvailable" },
];

export function SentEInvoices({ LinkComponent, onInvoiceClick, onCreditNoteClick, registered }: SentEInvoicesProps = {}) {
  const api = useDolfinClient();
  const [deliveries, setDeliveries] = useState<SentEInvoice[]>([]);
  const [filter, setFilter] = useState<DeliveryState | undefined>(undefined);
  const [reference, setReference] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Keyset pages: the stack holds the cursor of every page visited, so Previous is a pop. Filters and a
  // search reset to page one, because a cursor points into one filtered ordering and means nothing in another.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const cursor = cursorStack[cursorStack.length - 1];

  useEffect(() => {
    // A newer read abandons the one in flight, so a slow page can't paint over a quicker one.
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data, error } = await api.GET("/v1/outbound-e-invoices", {
          params: { query: { cursor, limit: PAGE_SIZE, state: filter, networkReference: reference } },
        });
        if (cancelled) return;
        if (error) {
          toastApiError(error, "Failed to load sent e-invoices.");
          setDeliveries([]);
          setNextCursor(null);
          setFailed(true);
          return;
        }
        setFailed(false);
        setDeliveries(data?.data ?? []);
        setNextCursor(data?.meta?.cursors?.next ?? null);
      } catch (e) {
        if (!cancelled) {
          toastApiError(e, "Failed to load sent e-invoices.");
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, filter, reference, cursor, reloadKey]);

  const openRow = (d: SentEInvoice) =>
    d.documentKind === "CreditNote" && d.creditNoteId && onCreditNoteClick
      ? () => onCreditNoteClick(d.creditNoteId!)
      : d.invoiceId && onInvoiceClick
        ? () => onInvoiceClick(d.invoiceId!)
        : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={filter ?? ALL}
          onValueChange={(v) => {
            setFilter(v === ALL ? undefined : (v as DeliveryState));
            setCursorStack([]);
          }}
        >
          <TabsList className="flex-wrap">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.label} value={f.state ?? ALL}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <EInvoiceReferenceSearch
          value={reference}
          onChange={(v) => {
            setReference(v);
            setCursorStack([]);
          }}
        />
      </div>

      {loading && deliveries.length === 0 ? (
        <LoadingState label="Loading sent documents..." />
      ) : failed ? (
        <ErrorState message="Sent documents could not be loaded." onRetry={() => setReloadKey((k) => k + 1)} />
      ) : deliveries.length === 0 ? (
        <EmptyState
          icon={Globe}
          title={reference ? "No match" : filter ? "Nothing in this state" : "Nothing sent yet"}
        >
          {reference
            ? `Nothing sent with network id ${reference}.`
            : filter
              ? "No documents are in this state right now."
              : registered === false
                ? "This organisation is not registered for e-invoicing yet. Once it is, invoices you send go over the network and appear here."
                : "Invoices you send go over the network first and appear here."}
        </EmptyState>
      ) : (
        <TableShell className={loading ? "opacity-60 transition-opacity" : undefined}>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Document</TableHead>
                <TableHead className={TH}>Sent to</TableHead>
                <TableHead className={TH}>Delivery</TableHead>
                <TableHead className={TH}>Last update</TableHead>
                <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((d) => {
                const link = rowLink(openRow(d));
                const recipient = recipientLabel(d.recipientScheme, d.recipientAddress);
                const when = d.deliveredAt
                  ? { label: "Delivered", at: d.deliveredAt }
                  : d.failedAt
                    ? { label: "Failed", at: d.failedAt }
                    : d.submittedAt
                      ? { label: "Sent", at: d.submittedAt }
                      : null;
                return (
                  <TableRow key={d.id} {...link} className={cn("align-top", link.className)}>
                    <TableCell className="max-w-[20rem]">
                      <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        {d.documentKind === "CreditNote" && d.creditNoteId && onCreditNoteClick ? (
                          <button
                            type="button"
                            onClick={() => onCreditNoteClick(d.creditNoteId!)}
                            className="cursor-pointer font-semibold hover:underline"
                          >
                            {d.documentNumber ?? "Credit note"}
                          </button>
                        ) : (
                          <InvoiceLink
                            invoiceId={d.invoiceId}
                            LinkComponent={LinkComponent}
                            onInvoiceClick={onInvoiceClick}
                          >
                            {d.documentNumber ?? "Invoice"}
                          </InvoiceLink>
                        )}
                        {d.documentKind === "CreditNote" && (
                          <Badge variant="outline" className="font-normal">
                            Credit note
                          </Badge>
                        )}
                        {/* A document that reached a simulator and not a tax authority has to say so,
                            or a successful test is indistinguishable from a filed invoice. */}
                        {d.sandbox && (
                          <Badge
                            variant="outline"
                            className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
                          >
                            Test
                          </Badge>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {d.network ? (d.network === "Sdi" ? "SDI" : "Peppol") : "Network"}
                        {d.country ? ` · ${d.country}` : ""}
                        {d.networkReference ? (
                          <span className="font-mono">
                            {" · "}
                            {d.networkReference}
                          </span>
                        ) : null}
                      </div>
                      {(d.faults ?? []).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {(d.faults ?? []).map((fault, i) => (
                            <li
                              key={`${fault.code ?? ""}-${i}`}
                              className="flex items-start gap-1.5 text-xs whitespace-normal text-muted-foreground"
                            >
                              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                              <span>
                                {fault.step ? <span className="font-medium">{fault.step}: </span> : null}
                                {fault.message}
                                {fault.code ? <span className="opacity-70"> ({fault.code})</span> : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[16rem]">
                      <EntityCell
                        name={d.customerName}
                        fallback="Unknown customer"
                        secondary={recipient ? <span className="font-mono">{recipient}</span> : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <DeliveryStateBadge state={d.state} />
                        {Number(d.attempt) > 1 && (
                          <span className="text-xs text-muted-foreground tabular-nums">Attempt {d.attempt}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                      {when ? (
                        <>
                          <div className="text-foreground">{when.label}</div>
                          <div className="text-xs tabular-nums">{formatDateTime(when.at)}</div>
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium whitespace-nowrap tabular-nums">
                      {/* formatCurrency renders bare for an unknown currency, so a dash only for a missing amount. */}
                      {d.totalAmount != null ? formatCurrency(d.totalAmount, d.currency) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableShell>
      )}

      <Pager
        page={cursorStack.length + 1}
        hasPrevious={cursorStack.length > 0 && !loading}
        hasNext={!!nextCursor && !loading}
        onPrevious={() => setCursorStack((stack) => stack.slice(0, -1))}
        onNext={() => nextCursor && setCursorStack((stack) => [...stack, nextCursor])}
      />
    </div>
  );
}

/** The channel named, then the address: the address alone doesn't say which network it belongs to. */
function recipientLabel(scheme?: string | null, address?: string | null) {
  if (!address) return null;
  const channel =
    scheme === "it-sdi-code"
      ? "SDI code"
      : scheme === "it-sdi-pec"
        ? "PEC"
        : scheme === "peppol"
          ? "Peppol"
          : scheme;
  return channel ? `${channel} ${address}` : address;
}

function InvoiceLink({
  invoiceId,
  LinkComponent,
  onInvoiceClick,
  children,
}: SentEInvoicesProps & { invoiceId?: string; children: ReactNode }) {
  const className = "font-semibold hover:underline";
  const href = `/invoices/${invoiceId}`;

  if (!invoiceId) return <span className="font-semibold">{children}</span>;
  if (onInvoiceClick) {
    return (
      <button type="button" onClick={() => onInvoiceClick(invoiceId)} className={`${className} cursor-pointer`}>
        {children}
      </button>
    );
  }
  if (LinkComponent) {
    return (
      <LinkComponent href={href} className={className}>
        {children}
      </LinkComponent>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

export function SentEInvoicesPage({ LinkComponent, onInvoiceClick, onCreditNoteClick, registered }: SentEInvoicesProps = {}) {
  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="E-invoices"
        subtitle="Invoices and credit notes you have sent over an e-invoicing network, and how each one was received. Delivered means the network accepted the document, not that the customer has approved it."
      />
      <SentEInvoices
        LinkComponent={LinkComponent}
        onInvoiceClick={onInvoiceClick}
        onCreditNoteClick={onCreditNoteClick}
        registered={registered}
      />
    </PageContainer>
  );
}
